// High-level cache operations for the offline mail layer. Sits between the
// raw IDB wrapper (db.js) and the UI/api callers (messageList, conversation).
//
// All keys are scoped by account slot so signing into a second account never
// leaks the first account's cached mail.

import { idbGet, idbPut, idbDelete, idbGetAll, idbCount, idbEvictByIndex } from "./db.js";

// Number of folders we'll keep cached at a time. LRU-evict beyond this.
const MAX_FOLDERS  = 10;
// Hard cap on individually-cached message bodies. LRU-evict by cachedAt.
const MAX_BODIES   = 500;
// Stale threshold - pages older than this are still served but refetched
// aggressively. Bodies don't expire (they're immutable once received).
export const PAGE_STALE_MS = 60_000;

// ──────────────────────────────────────────────────────────────── pages ────

export async function getCachedPage({ slot, folder, mode, pageIndex }) {
  try {
    return await idbGet("pages", [slot, folder, mode, pageIndex]) ?? null;
  } catch { return null; }
}

export async function putCachedPage(page) {
  try {
    await idbPut("pages", { ...page, cachedAt: Date.now() });
    await touchFolder(page.slot, page.folder, page.mode);
  } catch { /* cache write failures must never break the app */ }
}

// ── localStorage fallback for page 0 ───────────────────────────────────────
// IndexedDB is the PRIMARY offline store, but in the real world it can be
// unavailable entirely - private-mode Firefox throws on open, locked-down
// Safari / installed PWAs can block or evict it, a wedged profile times out.
// When that happens the user (correctly) sees "no cached mail" even though they
// browsed online, because nothing ever persisted. To guarantee the LANDING
// view still opens offline, we ALSO mirror page 0 of each visited folder into
// localStorage: tiny, synchronous, and available even where IndexedDB isn't.
// Only pageIndex 0 is mirrored, capped at a handful of folders (bounded size).
const LS_PAGE_PREFIX = "mc_page0";
const LS_MAX_FOLDERS = 8;
function lsPageKey(slot, folder, mode) { return `${LS_PAGE_PREFIX}_${slot}_${folder}_${mode}`; }

export function getPageLS({ slot, folder, mode, pageIndex }) {
  if (pageIndex !== 0) return null;
  try {
    const raw = localStorage.getItem(lsPageKey(slot, folder, mode));
    const rec = raw ? JSON.parse(raw) : null;
    return rec && Array.isArray(rec.items) ? rec : null;
  } catch { return null; }
}

export function putPageLS(page) {
  if (page.pageIndex !== 0) return;
  const write = () => localStorage.setItem(
    lsPageKey(page.slot, page.folder, page.mode),
    JSON.stringify({ ...page, cachedAt: Date.now() }),
  );
  try { write(); evictPageLS(); }
  catch {
    // Quota hit - drop the oldest mirrors and try once more, then give up
    // (IndexedDB, when it works, still holds the full cache).
    try { evictPageLS(true); write(); } catch { /* best effort */ }
  }
}

function evictPageLS(aggressive = false) {
  try {
    const found = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(LS_PAGE_PREFIX + "_")) continue;
      let ts = 0;
      try { ts = JSON.parse(localStorage.getItem(k))?.cachedAt ?? 0; } catch { /* corrupt */ }
      found.push({ k, ts });
    }
    const limit = aggressive ? Math.max(1, LS_MAX_FOLDERS - 3) : LS_MAX_FOLDERS;
    if (found.length <= limit) return;
    found.sort((a, b) => a.ts - b.ts); // oldest first
    for (const { k } of found.slice(0, found.length - limit)) localStorage.removeItem(k);
  } catch { /* best effort */ }
}

// Drop every cached page for a given folder (used when its mode flips, e.g.
// switching from threaded to flat after enabling search).
export async function clearFolderPages(slot, folder) {
  try { localStorage.removeItem(lsPageKey(slot, folder, "flat")); } catch { /* */ }
  try { localStorage.removeItem(lsPageKey(slot, folder, "threaded")); } catch { /* */ }
  try {
    const all = await idbGetAll("pages");
    for (const p of all) {
      if (p.slot === slot && p.folder === folder) {
        await idbDelete("pages", [p.slot, p.folder, p.mode, p.pageIndex]);
      }
    }
  } catch { /* swallow */ }
}

// ─────────────────────────────────────────────────────────────── bodies ────

// IMAP UIDs are unique only WITHIN a folder, so the folder is part of the key.
// Without it, UID 29 in Sent and UID 29 in INBOX/Newsletters shared one cache
// entry and the wrong message body was shown.
export async function getCachedBody(slot, folder, uid) {
  if (!folder) return null;          // never guess a folder - a miss is safe, a wrong hit is not
  try {
    return await idbGet("bodies", [slot, folder, Number(uid)]) ?? null;
  } catch { return null; }
}

export async function putCachedBody(slot, folder, body) {
  if (!body || body.uid == null || !folder) return;
  try {
    await idbPut("bodies", { ...body, slot, folder, uid: Number(body.uid), cachedAt: Date.now() });
    // Lazy eviction: every ~20 writes we check the bodies count.
    if (Math.random() < 0.05) await maybeEvictBodies();
  } catch { /* swallow */ }
}

async function maybeEvictBodies() {
  try {
    const n = await idbCount("bodies");
    if (n > MAX_BODIES) await idbEvictByIndex("bodies", "cachedAt", n - MAX_BODIES);
  } catch { /* swallow */ }
}

// ─────────────────────────────────────────────────────────────── folder LRU ─

async function touchFolder(slot, folder, mode) {
  try {
    await idbPut("folders", { slot, folder, mode, lastAccessedAt: Date.now() });
    await maybeEvictFolders();
  } catch { /* swallow */ }
}

async function maybeEvictFolders() {
  try {
    const all = await idbGetAll("folders");
    if (all.length <= MAX_FOLDERS) return;
    all.sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
    const overflow = all.slice(0, all.length - MAX_FOLDERS);
    for (const f of overflow) {
      // Drop the folder record AND any pages under it.
      const pages = await idbGetAll("pages");
      for (const p of pages) {
        if (p.slot === f.slot && p.folder === f.folder) {
          await idbDelete("pages", [p.slot, p.folder, p.mode, p.pageIndex]);
        }
      }
      await idbDelete("folders", [f.slot, f.folder]);
    }
  } catch { /* swallow */ }
}
